import { logNonFatal } from '~/lib/diagnostics'
import type {
  CatalogueContent,
  CatalogueIdentity,
  CatalogueProduct,
} from '~/services/catalogue'
import type { CatalogueGridSlot } from '~/store/catalogue-grid'

export type ShopTab = 'featured' | 'classic' | 'currency'

// 'top_up' is the external contract name for the Coins & Crystals tab and
// 'classic' for the Classic tab; any other value (including the internal
// 'currency') falls back to the Featured tab, the default landing
// (GLOSSARY.md "Shop launch deep link").
function resolveTabParam(tabParam: string | null): ShopTab {
  if (tabParam === 'top_up') return 'currency'
  if (tabParam === 'classic') return 'classic'
  return 'featured'
}

// The tab Shop lands on, from the launch parameters alone. A product
// parameter, judged by presence as its precedence is, outranks `tab` and
// lands on Classic: under its detail when the target resolves, as a normal
// Classic open when it resolves to no visible Classic product (GLOSSARY.md
// "Shop launch deep link"). One answer for the boot skeleton's shape, the
// reveal surface, the hydration order, and the shell's first tab, so the
// skeleton and the revealed tab always agree (ADR 0048).
export function resolveLandingTab(
  tabParam: string | null,
  launchProduct: LaunchProduct | undefined,
): ShopTab {
  return launchProduct === undefined ? resolveTabParam(tabParam) : 'classic'
}

// The launch URL's product parameter (GLOSSARY.md "Shop launch deep link"):
// the content kind it names and its raw value, kept raw so an unresolved
// one is reported exactly as sent.
export interface LaunchProduct {
  content: CatalogueContent
  value: string
}

const launchProductParamName = {
  land_skin: 'landskin_id',
  tree_type: 'tree_id',
} as const satisfies Record<CatalogueContent, string>

const unresolvedEvent = {
  land_skin: 'shop_deep_link_land_skin_unresolved',
  tree_type: 'shop_deep_link_tree_unresolved',
} as const satisfies Record<CatalogueContent, string>

// The product parameter a launch honors: tree_id over landskin_id, judged by
// presence rather than validity — a malformed tree_id still outranks a
// landskin_id, and both present is a malformed launch URL, reported once and
// never fallen back from (GLOSSARY.md "Shop launch deep link").
export function selectLaunchProduct(
  treeIdParam: string | null,
  landSkinIdParam: string | null,
): LaunchProduct | undefined {
  if (treeIdParam !== null && landSkinIdParam !== null) {
    logNonFatal('shop_deep_link_conflict', {
      landskin_id: landSkinIdParam,
      tree_id: treeIdParam,
    })
  }
  if (treeIdParam !== null) return { content: 'tree_type', value: treeIdParam }
  if (landSkinIdParam !== null) {
    return { content: 'land_skin', value: landSkinIdParam }
  }
  return undefined
}

// The launch product as diagnostics params — `tree_id` / `landskin_id` with
// the raw value — for every report about the launch; nothing without one.
export function launchProductParams(
  launch: LaunchProduct | undefined,
): Record<string, string> {
  if (!launch) return {}
  return { [launchProductParamName[launch.content]]: launch.value }
}

// The catalogue identity a launch product names — the content kind's gid,
// never a Store product id. undefined when the value is not a gid at all.
export type DeepLinkTarget = CatalogueIdentity

export function parseDeepLinkTarget(
  launch: LaunchProduct | undefined,
): DeepLinkTarget | undefined {
  if (launch === undefined || !/^\d+$/.test(launch.value)) return undefined
  return { content: launch.content, gid: Number(launch.value) }
}

// A product parameter names one kind: the other kind sharing the gid number
// is never the target (GLOSSARY.md "Shop launch deep link"). Every lookup of
// the target — the priority hydration, the reveal predicate, the resolution
// below — goes through here so none of them can match on gid alone.
export function isDeepLinkTarget(
  slot: CatalogueIdentity,
  target: DeepLinkTarget,
): boolean {
  return slot.content === target.content && slot.gid === target.gid
}

export type DeepLinkResolution =
  | { status: 'pending' }
  | { status: 'resolved'; product: CatalogueProduct }
  | { status: 'unresolved' }

// Resolves the launch product against the slot grid. 'pending' while the
// target's slot still hydrates; a value that maps to no visible Classic
// product — no slot, or a slot that settled omitted — degrades to a normal
// Shop open and emits the kind's own diagnostics.
export function resolveDeepLinkProduct(
  launch: LaunchProduct,
  slots: CatalogueGridSlot[],
): DeepLinkResolution {
  const target = parseDeepLinkTarget(launch)
  const slot =
    target === undefined
      ? undefined
      : slots.find((entry) => isDeepLinkTarget(entry, target))
  if (slot?.product) return { status: 'resolved', product: slot.product }
  if (slot?.status === 'pending') return { status: 'pending' }
  logNonFatal(unresolvedEvent[launch.content], launchProductParams(launch))
  return { status: 'unresolved' }
}
